import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, Calendar, Clock, Receipt, DollarSign, FileText, 
  Award, TrendingUp, UserPlus, ClipboardCheck, Building2,
  ArrowUpRight, ArrowDownRight, Activity, Bell, Plus
} from 'lucide-react';
import hrApiClient from '@/api/hrClient';

export default function HRDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalEmployees: 156,
    activeToday: 142,
    onLeave: 8,
    pendingClaims: 5,
    openPositions: 7,
    monthlyPayroll: 245000,
    pendingApprovals: 12,
    upcomingBirthdays: 3
  });
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([
    { id: 1, type: 'leave', title: 'John Doe requested annual leave', time: '2 hours ago', status: 'pending' },
    { id: 2, type: 'hire', title: 'Sarah Chen accepted offer letter', time: '5 hours ago', status: 'completed' },
    { id: 3, type: 'attendance', title: 'Team meeting attendance recorded', time: '1 day ago', status: 'completed' },
    { id: 4, type: 'review', title: 'Q4 performance reviews due', time: '2 days ago', status: 'pending' },
  ]);

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await hrApiClient.get('/dashboard/stats');
      if (response.data) {
        setStats(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Using default stats');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Employees', 
      value: stats.totalEmployees, 
      change: '+12 this month',
      changeType: 'positive',
      icon: Users, 
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10',
      textColor: 'text-blue-400',
      href: '/suite/hr/employees' 
    },
    { 
      title: 'Active Today', 
      value: stats.activeToday, 
      subtext: `${Math.round((stats.activeToday / stats.totalEmployees) * 100)}% attendance`,
      icon: Clock, 
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/10',
      textColor: 'text-green-400',
      href: '/suite/hr/attendance' 
    },
    { 
      title: 'On Leave', 
      value: stats.onLeave, 
      subtext: `${stats.pendingApprovals} pending approvals`,
      icon: Calendar, 
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-500/10',
      textColor: 'text-amber-400',
      href: '/suite/hr/leave' 
    },
    { 
      title: 'Open Positions', 
      value: stats.openPositions, 
      subtext: 'Actively recruiting',
      icon: UserPlus, 
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-500/10',
      textColor: 'text-pink-400',
      href: '/suite/hr/employees' 
    },
    { 
      title: 'Pending Claims', 
      value: stats.pendingClaims, 
      subtext: 'Awaiting approval',
      icon: Receipt, 
      color: 'from-purple-500 to-violet-500',
      bgColor: 'bg-purple-500/10',
      textColor: 'text-purple-400',
      href: '/suite/hr/claims' 
    },
    { 
      title: 'Monthly Payroll', 
      value: `RM ${stats.monthlyPayroll?.toLocaleString() || 0}`, 
      subtext: 'Next run: 25th',
      icon: DollarSign, 
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-500/10',
      textColor: 'text-emerald-400',
      href: '/suite/hr/payroll' 
    },
  ];

  const quickActions = [
    { label: 'Add Employee', icon: UserPlus, onClick: () => navigate('/suite/hr/employees'), color: 'bg-blue-600 hover:bg-blue-700' },
    { label: 'Process Payroll', icon: DollarSign, onClick: () => navigate('/suite/hr/payroll'), color: 'bg-emerald-600 hover:bg-emerald-700' },
    { label: 'Review Leaves', icon: Calendar, onClick: () => navigate('/suite/hr/leave'), color: 'bg-amber-600 hover:bg-amber-700' },
    { label: 'View Reports', icon: TrendingUp, onClick: () => navigate('/suite/hr/reports'), color: 'bg-purple-600 hover:bg-purple-700' },
  ];

  const departmentData = [
    { name: 'Engineering', count: 45, color: 'bg-blue-500' },
    { name: 'Sales', count: 32, color: 'bg-green-500' },
    { name: 'Marketing', count: 18, color: 'bg-purple-500' },
    { name: 'HR', count: 12, color: 'bg-orange-500' },
    { name: 'Finance', count: 15, color: 'bg-pink-500' },
    { name: 'Operations', count: 34, color: 'bg-cyan-500' },
  ];

  const getActivityIcon = (type) => {
    switch(type) {
      case 'leave': return <Calendar className="w-5 h-5 text-amber-400" />;
      case 'hire': return <UserPlus className="w-5 h-5 text-green-400" />;
      case 'attendance': return <Clock className="w-5 h-5 text-blue-400" />;
      case 'review': return <Award className="w-5 h-5 text-purple-400" />;
      default: return <Activity className="w-5 h-5 text-slate-400" />;
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">HR Dashboard</h1>
          <p className="text-slate-400 mt-1">Manage your workforce and HR operations</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/suite/hr/employees')} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat) => (
          <Card 
            key={stat.title} 
            className="bg-slate-900 border-slate-800 cursor-pointer hover:border-slate-600 transition-all hover:scale-[1.02]"
            onClick={() => navigate(stat.href)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 uppercase tracking-wider">{stat.title}</p>
                  <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                  {stat.subtext && (
                    <p className="text-sm text-slate-500 mt-1">{stat.subtext}</p>
                  )}
                  {stat.change && (
                    <p className={`text-sm mt-1 flex items-center gap-1 ${stat.changeType === 'positive' ? 'text-green-400' : 'text-red-400'}`}>
                      {stat.changeType === 'positive' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      {stat.change}
                    </p>
                  )}
                </div>
                <div className={`w-14 h-14 rounded-2xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-7 h-7 ${stat.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Department Distribution */}
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              By Department
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {departmentData.map((dept) => (
                <div key={dept.name} className="p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-3 h-3 rounded-full ${dept.color}`} />
                    <p className="text-sm text-slate-300">{dept.name}</p>
                  </div>
                  <p className="text-2xl font-bold text-white">{dept.count}</p>
                  <p className="text-xs text-slate-500">employees</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                onClick={action.onClick}
                className={`w-full justify-start ${action.color}`}
              >
                <action.icon className="w-4 h-4 mr-3" />
                {action.label}
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-white">{activity.title}</p>
                  <p className="text-xs text-slate-500">{activity.time}</p>
                </div>
                <Badge className={activity.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}>
                  {activity.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
